// White-label — derivazione dei colori di marca (WB-R22 / ADR-004).
//
// La sorgente memorizza SOLO `primaryColor` e `secondaryColor`. Tutte le
// varianti effettivamente usate a runtime (dark, contrasto-safe, variante per
// fondo solido con testo bianco) sono derivate qui, in un unico modulo condiviso
// da mobile e web così i due lati non divergono.
//
// La logica `safeAccent` è quella storicamente in
// apps/mobile/src/theme/ThemeContext.tsx: rimappa i preset noti a varianti già
// calibrate per il contrasto AA in light/dark, e lascia invariati i colori
// arbitrari (per quelli ci pensa `darkenForBg`).

export type ColorScheme = 'dark' | 'light';

export type BrandingFontFamily = 'inter' | 'system' | 'lora' | 'roboto' | 'montserrat';

export const BRANDING_FONT_FAMILIES: BrandingFontFamily[] = [
	'inter',
	'system',
	'lora',
	'roboto',
	'montserrat',
];

/** Preset di colore offerti nel pannello di configurazione. */
export const BRANDING_COLOR_PRESETS = [
	'#00E5FF',
	'#3B82F6',
	'#7C3AED',
	'#10B981',
	'#F59E0B',
	'#EF4444',
	'#EC4899',
	'#14B8A6',
] as const;

// Light-mode-safe — campionati dagli swatch del designer.
const LIGHT_SAFE_ACCENT: Record<string, string> = {
	'#00E5FF': '#5EA1D4',
	'#7C3AED': '#9D80F9',
	'#10B981': '#14B078',
	'#F59E0B': '#F4940E',
	'#EF4444': '#B91C1C',
	'#3B82F6': '#31B5F7',
	'#60A5FA': '#31B5F7',
	'#9064F7': '#9D80F9',
	'#12E28F': '#14B078',
};

// Dark-mode-safe — più accesi mantenendo ≥ 4.5:1 su #050D1A.
const DARK_SAFE_ACCENT: Record<string, string> = {
	'#7C3AED': '#9064F7',
	'#10B981': '#12E28F',
	'#3B82F6': '#60A5FA',
	'#5B21B6': '#9064F7',
	'#047857': '#12E28F',
	'#1D4ED8': '#60A5FA',
};

/**
 * Rimappa un colore di marca/preset alla variante con contrasto leggibile
 * effettivamente applicata a runtime per lo schema dato. I colori non presenti
 * nelle mappe (brand arbitrari) sono restituiti invariati.
 */
export function safeAccent(color: string, scheme: ColorScheme): string {
	const upper = color.toUpperCase();
	if (scheme === 'light') return LIGHT_SAFE_ACCENT[upper] ?? color;
	return DARK_SAFE_ACCENT[upper] ?? color;
}

// ── hex ⇄ hsl ───────────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] | null {
	let h = hex.replace('#', '').trim();
	if (h.length === 3) {
		h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
	}
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
	return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
	const c = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, '0');
	return `#${c(r)}${c(g)}${c(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			default:
				h = (r - g) / d + 4;
		}
		h /= 6;
	}
	return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
	if (s === 0) return [l * 255, l * 255, l * 255];
	const hue2rgb = (p: number, q: number, t: number) => {
		if (t < 0) t += 1;
		if (t > 1) t -= 1;
		if (t < 1 / 6) return p + (q - p) * 6 * t;
		if (t < 1 / 2) return q;
		if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
		return p;
	};
	const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const p = 2 * l - q;
	return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}

/** Luminanza relativa WCAG di un hex (0..1). */
export function relativeLuminance(hex: string): number {
	const rgb = hexToRgb(hex);
	if (!rgb) return 0;
	const [r, g, b] = rgb.map((v) => {
		const c = v / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	});
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Rapporto di contrasto WCAG fra due hex. */
export function contrastRatio(a: string, b: string): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Scurisce un colore finché non regge il testo bianco (contrasto ≥ 4.5:1),
 * preservandone la tinta. Usato per la variante "fondo solido con testo bianco"
 * quando il brand è un colore arbitrario non coperto dalle mappe safe.
 */
export function darkenForBg(hex: string): string {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const [h, s0, l0] = rgbToHsl(...rgb);
	let s = s0;
	let l = l0;
	let out = hex;
	for (let i = 0; i < 20 && contrastRatio(out, '#FFFFFF') < 4.5; i++) {
		l = Math.max(0, l - 0.03);
		s = Math.min(1, s + 0.01);
		out = rgbToHex(...hslToRgb(h, s, l));
	}
	return out;
}

// ── API principale ──────────────────────────────────────────────────────────

export interface DerivedBrandColors {
	/** Accent per testo/icone/bordi, contrasto-safe per lo schema. */
	primaryColor: string;
	/** Variante per fondo solido con testo bianco (bottoni/badge). */
	primaryColorDark: string;
	/** Colore secondario di marca, contrasto-safe. */
	accentPurple: string;
	/** Derivato dal secondario per gli accent "positivi" — mantenuto per compat. */
	accentGreen: string;
}

/**
 * Deriva la palette runtime a partire dai due soli colori memorizzati.
 * `scheme` seleziona le varianti light/dark.
 */
export function deriveBrandColors(
	input: { primaryColor: string; secondaryColor: string },
	scheme: ColorScheme,
): DerivedBrandColors {
	const primary = safeAccent(input.primaryColor, scheme);
	const secondary = safeAccent(input.secondaryColor, scheme);
	return {
		primaryColor: primary,
		primaryColorDark: darkenForBg(safeAccent(input.primaryColor, 'light')),
		accentPurple: secondary,
		accentGreen: primary,
	};
}
